import React, { useState } from 'react';
import {
  ShareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface SocialSharingCardProps {
  countryName: string;
  description?: string;
  imageUrl?: string;
  variant?: 'card' | 'banner';
}

const SocialSharingCard: React.FC<SocialSharingCardProps> = React.memo(({
  countryName,
  description,
  imageUrl,
  variant = 'card',
}) => {
  const [copied, setCopied] = useState(false);
  const currentUrl = window.location.href;

  // Check if Web Share API is available
  const canShare = typeof navigator !== 'undefined' && navigator.share;

  const shareData = {
    title: `${countryName} - Travel Destination`,
    text: description || `Explore ${countryName} with our travel packages and group trips`,
    url: currentUrl,
  };

  const handleWebShare = async () => {
    if (canShare) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled or error occurred
        console.error('Error sharing:', error);
      }
    }
  };

  const handleFacebookShare = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const handleTwitterShare = () => {
    const text = `Check out ${countryName}! ${description || ''}`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(currentUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const handleWhatsAppShare = () => {
    const text = `Check out ${countryName}! ${currentUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (variant === 'banner') {
    return (
      <div className="bg-white py-12 border-y border-gray-100 italic">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl md:text-3xl font-playfair font-bold text-gray-900 mb-4">
            Share this destination
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Inspire your friends and family with the beauty of {countryName}
          </p>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6">
            {/* Web Share API Button (if available) */}
            {canShare && (
              <button
                onClick={handleWebShare}
                className="flex items-center justify-center gap-3 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
                aria-label="Share via system share menu"
              >
                <ShareIcon className="w-6 h-6" />
                <span className="font-semibold">Share Now</span>
              </button>
            )}

            {/* Facebook */}
            <button
              onClick={handleFacebookShare}
              className="flex items-center justify-center gap-3 px-6 py-3 bg-[#1877F2] text-white rounded-xl hover:bg-[#166FE5] transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
              aria-label={`Share ${countryName} on Facebook`}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="font-semibold">Facebook</span>
            </button>

            {/* Twitter/X */}
            <button
              onClick={handleTwitterShare}
              className="flex items-center justify-center gap-3 px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
              aria-label={`Share ${countryName} on Twitter`}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span className="font-semibold">Twitter</span>
            </button>

            {/* WhatsApp */}
            <button
              onClick={handleWhatsAppShare}
              className="flex items-center justify-center gap-3 px-6 py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#20BD5A] transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
              aria-label={`Share ${countryName} on WhatsApp`}
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
              </svg>
              <span className="font-semibold">WhatsApp</span>
            </button>

            {/* Copy Link */}
            <button
              onClick={handleCopyLink}
              className="flex items-center justify-center gap-3 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
              aria-label={copied ? "Link copied to clipboard" : "Copy link to clipboard"}
            >
              {copied ? (
                <>
                  <CheckIcon className="w-6 h-6 text-green-600" aria-hidden="true" />
                  <span className="font-semibold text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="font-semibold">Copy Link</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Share This Destination
      </h3>

      <div className="flex flex-wrap gap-3">
        {/* Web Share API Button (if available) */}
        {canShare && (
          <button
            onClick={handleWebShare}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 shadow-sm hover:shadow-md"
            aria-label="Share via system share menu"
          >
            <ShareIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Share</span>
          </button>
        )}

        {/* Facebook */}
        <button
          onClick={handleFacebookShare}
          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] min-w-[44px] bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors duration-200 shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-[#1877F2] focus-visible:ring-offset-2"
          aria-label={`Share ${countryName} on Facebook`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          <span className="text-sm font-medium hidden sm:inline">Facebook</span>
        </button>

        {/* Twitter/X */}
        <button
          onClick={handleTwitterShare}
          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] min-w-[44px] bg-black text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-gray-800 focus-visible:ring-offset-2"
          aria-label={`Share ${countryName} on Twitter`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          <span className="text-sm font-medium hidden sm:inline">Twitter</span>
        </button>

        {/* WhatsApp */}
        <button
          onClick={handleWhatsAppShare}
          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] min-w-[44px] bg-[#25D366] text-white rounded-lg hover:bg-[#20BD5A] transition-colors duration-200 shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
          aria-label={`Share ${countryName} on WhatsApp`}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
          </svg>
          <span className="text-sm font-medium hidden sm:inline">WhatsApp</span>
        </button>

        {/* Copy Link */}
        <button
          onClick={handleCopyLink}
          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] min-w-[44px] bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
          aria-label={copied ? "Link copied to clipboard" : "Copy link to clipboard"}
          aria-live="polite"
        >
          {copied ? (
            <>
              <CheckIcon className="w-5 h-5 text-green-600" aria-hidden="true" />
              <span className="text-sm font-medium text-green-600">Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-sm font-medium hidden sm:inline">Copy Link</span>
            </>
          )}
        </button>
      </div>

      <p className="mt-4 text-xs text-gray-500 text-center">
        Share this destination with friends and family
      </p>
    </div>
  );
});

SocialSharingCard.displayName = 'SocialSharingCard';

export default SocialSharingCard;
